(ns minorodata.recipes.api
  (:require [re-frame.core :as re-frame]
            [re-posh.core :as re-posh]
            [applied-science.js-interop :as js-interop]
            [mold-http.core :refer [reg-event-http]]
            [minorodata.utils :as utils]
            [minorodata.app.utils :as app.utils]
            [minorodata.app.handlers :refer [reg-event-fx]]
            [minorodata.recipes.model :refer [->recipe]]
            [clojure.set]
            [clojure.string]
            ["dagre" :as dagre]))


;; =============================================================================
;; Fetch recipes list
;; =============================================================================

(defn recipes-request [_ _]
  {:http-xhrio
   {:id  :recipes-request
    :url "/api/transformations/recipes"}})

(defn recipes-callback [_ [error results]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch recipes"]}
    (let [raw-recipes (get-in results [:results])
          recipes     (map ->recipe raw-recipes)
          old-recipes @(re-posh/subscribe [:recipe/pull-many])]

      {:dispatch-n (conj (mapv #(vector :recipe/delete (:db/id %))
                               old-recipes)
                         [:recipe/add-many recipes])})))

(reg-event-http
 :recipes.api/fetch-recipes
 recipes-request
 recipes-callback)


(re-frame/reg-event-fx
 :recipes.events/load-data
 (fn [_ _]
   {:dispatch-n [[:connections.api/fetch-sources]
                 [:connections.api/fetch-user-connectors]
                 [:recipes.api/fetch-recipes]]}))



;; =============================================================================
;; Helper to update the mini dags for the recipes
;; =============================================================================

(defn get-recipe-extracts [source-id recipe]
  (let [source      @(re-posh/subscribe [:source/pull [:source/source-id source-id]])
        type-string (first (:recipe/source-report-platform-type recipe))

        source-type (:source/source-type source)
        sources     @(re-posh/subscribe [:source/pull-many])
        same-type-sources (utils/find-all-by-field sources :source/source-type source-type)

        [_ recipe-type & _] (clojure.string/split type-string #",")


        all-extracts (map (fn [{:source/keys [source-id name]}]
                            (map #(assoc % :extract/source-name name)
                                 (:extracts @(re-posh/subscribe [:connections.subs/extracts-for-source source-id]))))
                          same-type-sources)

        extracts (-> all-extracts
                     concat
                     flatten)

        extract-check-pred (fn [extract]
                             (let [extract-conf (get-in extract [:extract/config :extracts 0])]
                              ;; default is :report-type, but some custom jumper connectors use :report also
                               (some #{recipe-type} [(:report-type extract-conf) (:report extract-conf)])))

        necessary-extracts (filter extract-check-pred extracts)

        placeholder-extract [{:extract/extract-id  "extract-id"
                              :extract/source-name "Source"
                              :extract/name        "Extract"}]]

    (map (fn [e]
           {:id            (:extract/extract-id e)
            :type          "extract"
            :source-title  (:extract/source-name e)
            :title         (:extract/name e)
            :output-tables (:extract/output-tables e)})
         (if (empty? necessary-extracts)
           placeholder-extract
           necessary-extracts))))

(defn templates->dag [templates recipe source-id]
  (let [input-tables (set (mapcat (fn [[_ v]]
                                    (get-in v [:input-tables :values]))
                                  templates))
        output-tables (set (mapcat (fn [[_ v]]
                                     (get-in v [:output-tables]))
                                   templates))

        extract-prefix "extract-id-"

        recipe-extracts (get-recipe-extracts source-id recipe)

        node-width 120
        node-gap   50

        extract-nodes (map (fn [{:keys [id type title output-tables source-title]}]
                             {:id (str extract-prefix id)
                              :data {:label title :type "extract" :source-title source-title}
                              :position {:y 0 :x -150}
                              :style {:width node-width}
                              :sourcePosition "right"
                              :targetPosition "left"
                              :type "extract"})
                           recipe-extracts)

        nodes (map-indexed (fn [idx [k v]]
                             (let [id    (name k)
                                   title (clojure.string/replace id #"-" " ")

                                   extract?   (= "extract" (get-in v [:input-tables :type]))

                                   last-node? (empty?
                                               (clojure.set/intersection
                                                (set (:output-tables v))
                                                input-tables))]

                               [{:id   id
                                 :data {:label title :type "transform" :input extract?}
                                 :position {:y 0 :x (* idx (+ node-width node-gap))}
                                 :style {:width node-width}
                                 :sourcePosition "right"
                                 :targetPosition "left"
                                 :type (if last-node?
                                        "final-tarnsform"
                                        "transform")}]))
                           templates)

        all-nodes (->> (concat nodes extract-nodes) flatten (filter some?))

        edges (mapcat (fn [[k v]]
                        (let [origin-id (name k)]
                          (mapcat (fn [output-table]
                                    (let [targets (filter (fn [[tid definition]]
                                                            (contains? (set (get-in definition [:input-tables :values])) output-table))
                                                          (into [] templates))]
                                      (map (fn [[tid _]]
                                             (let [target-id (name tid)]
                                               {:id (str origin-id target-id)
                                                :source origin-id
                                                :target target-id
                                                :animated true}))
                                           targets)))
                                  (:output-tables v))))
                      templates)


        extract-edges (->> (map (fn [{:keys [id]}]

                                    (let [connected-nodes (filter (fn [node]
                                                                    (get-in node [:data :input]))
                                                                  all-nodes)]

                                      (map (fn [node]
                                             {:id (str id (:id node))
                                              :source id
                                              :target (:id node)
                                              :animated true})
                                           connected-nodes)))
                                  extract-nodes)
                             flatten
                             (filter some?))


        all-edges (concat edges extract-edges)]
    {:nodes all-nodes
     :edges all-edges}))


(re-frame/reg-event-fx
 :recipes.events/update-mini-dag-layout
 (fn [{:keys [db]} [_ recipe-id source-id]]
   (let [recipe @(re-posh/subscribe [:recipe/pull [:recipe/recipe-id recipe-id]])
         templates        (:recipe/templates recipe)
         dag              (templates->dag templates recipe source-id)

         graph (dagre/graphlib.Graph.)]


     (js-interop/call graph :setGraph #js {:rankdir "LR"
                                           :nodesep 10})

     (js-interop/call graph :setDefaultEdgeLabel (fn [] #js {}))

     (doall
      (for [node (:nodes dag)]
        (js-interop/call graph
                         :setNode (:id node)
                         (clj->js (assoc node
                                         :width  120
                                         :height 100
                                         :label  (get-in node [:data :label]))))))

     (doall
      (for [edge (:edges dag)]
        (js-interop/call graph :setEdge (:source edge) (:target edge))))


     (js-interop/call dagre :layout graph)

     (let [node-ids (.nodes graph)
           nodes     (map (fn [node-id]
                            (let [node (js->clj (js-interop/call graph :node node-id) :keywordize-keys true)]
                              (-> node
                                  (assoc-in [:position :x] (:x node))
                                  (assoc-in [:position :y] (:y node)))))
                          node-ids)

           ;; Need to update this to use the proper webworkers and our elk algo
           ;; that we use for the big dag.
           g (js-interop/call graph :graph)

           dag-height (js-interop/get g :height)
           dag-width  (js-interop/get g :width)

           ordered-dag (assoc dag :nodes nodes :height dag-height :width dag-width)]

       {:db (assoc-in db [:recipe-mini-dags recipe-id source-id] ordered-dag)}))))

;; =============================================================================
;; Fetch recipe details
;; =============================================================================

(defn recipe-details-request [_ [recipe-id callback]]
  {:http-xhrio
   {:id        (str "recipe-details-request-" recipe-id)
    :url       (str "/api/transformations/recipes/" recipe-id)
    :cb-params {:recipe-id recipe-id :callback callback}}})

(defn recipe-details-callback [_ [error results {:keys [recipe-id callback]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch recipe details"]}
    (let [recipe (get-in results [:results])
          templates (:templates recipe)]


      {:dispatch [:recipe/update [:recipe/recipe-id recipe-id] {:recipe/templates templates}]

       ;; Run out of order
       :dispatch-later [(when callback {:ms 10 :dispatch callback})]})))

(reg-event-http
 :recipes.api/fetch-recipe-details
 recipe-details-request
 recipe-details-callback)


(re-frame/reg-event-fx
 :recipes.events/load-recipe-details
 (fn [_ [_ recipe-id]]
   {:async-flow
    {:first-dispatch [:recipes.api/fetch-recipe-details recipe-id]
     :rules          [{:when :seen?
                       :events :recipes.api/fetch-recipe-details-complete
                       :halt? true
                       :dispatch [:connector/add {:connector/action :create-recipe
                                                  :connector/type   recipe-id}]}]}}))


;; =============================================================================
;; Compile template
;; =============================================================================

(defn compile-template-callback [_ [error _ {:keys [on-close group-id]}]]
  (if (nil? error)
    (do
     (on-close)
     {:dispatch-n [[:app.events/navigate :transforms {:group-id group-id}]
                   [:app.events/add-snack :success "Successfully deployed the recipe"]]})
    (let [error-message (get-in error [:parse-error :original-text])]
      {:dispatch [:app.events/add-snack :error (if error-message
                                                 (str "Failed to deploy the recipe: " error-message)
                                                 "Failed to deploy the recipe")]})))

(defn compile-template-request [_ [recipe-id on-close params]]
  {:http-xhrio
   {:id     :compile-template-request
    :method :post
    :url    (str "/api/transformations/recipes/" recipe-id "/compile")
    :params params

    :cb-params {:on-close on-close
                :group-id (:group-id params)}}})

(reg-event-http
 :recipes.api/compile-template
 compile-template-request
 compile-template-callback)

;; =============================================================================
;; Compile multiple recipes
;; =============================================================================

(defn compile-multiple-recipes-callback [_ [error _ {:keys [params on-success]}]]
  (if (nil? error)
    (do
      (when on-success (on-success))
      {:dispatch-n [[:app.events/navigate :transforms {:group-id (some-> params first :group-id)}]
                    [:app.events/add-snack :success "Successfully deployed the recipe"]]})
    (let [error-message (get-in error [:parse-error :original-text])]
      {:dispatch [:app.events/add-snack :error (if error-message
                                                 (str "Failed to deploy the recipes: " error-message)
                                                 "Failed to deploy the recipes")]})))

(defn compile-multiple-recipes-request [_ [{:keys [params on-success]}]]
  {:http-xhrio
   {:id     :compile-recipes-request
    :method :post
    :url    (str "/api/transformations/recipes")
    :params {:recipes params}

    :cb-params {:on-success on-success
                :params     params}}})

(reg-event-http
 :recipes.api/compile-recipes
 compile-multiple-recipes-request
 compile-multiple-recipes-callback)
